import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Modal, message, Space, Popconfirm } from 'antd';
import { adminAPI } from '../api/admin';

const LANGUAGE_LABELS = {
  fa: 'فارسی',
  en: 'English',
  ja: '日本語',
  de: 'Deutsch',
  pt: 'Português',
  fr: 'Français',
  it: 'Italiano',
  es: 'Español',
  ko: '한국어',
  hi: 'हिन्दी',
  zh: '中文'
};

const languageLabel = (lang) => LANGUAGE_LABELS[lang] || lang || '—';

export default function ContactMessageManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    load();
  }, [page]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getContactMessages(page, 20);
      if (res.data?.success) {
        setItems(res.data.messages || []);
        setTotal(res.data.pagination?.total || 0);
        setUnreadCount(res.data.unreadCount || 0);
      }
    } catch (err) {
      message.error('خطا در بارگذاری پیام‌ها');
    } finally {
      setLoading(false);
    }
  };

  const openModal = async (item) => {
    setSelected(item);
    setModalOpen(true);
    if (!item.isRead) {
      try {
        await adminAPI.markContactMessageRead(item._id);
        // Refresh local read state
        setItems(prev => prev.map(msg => msg._id === item._id ? { ...msg, isRead: true } : msg));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Failed to mark message as read', err);
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await adminAPI.deleteContactMessage(id);
      if (res.data?.success) {
        message.success('پیام با موفقیت حذف شد');
        load();
      }
    } catch (err) {
      message.error('خطا در حذف پیام');
    }
  };

  const columns = [
    {
      title: 'فرستنده',
      key: 'sender',
      render: (_, record) => (
        <div>
          <strong>{record.name}</strong>
          <div style={{ color: '#888', fontSize: '12px' }}>{record.emailOrPhone}</div>
        </div>
      )
    },
    {
      title: 'موضوع',
      dataIndex: 'subject',
      key: 'subject',
      render: text => text || <span style={{ color: '#aaa', fontStyle: 'italic' }}>بدون موضوع</span>
    },
    {
      title: 'زبان',
      dataIndex: 'lang',
      key: 'lang',
      width: 80,
      render: l => <Tag color={l === 'fa' ? 'blue' : 'purple'}>{languageLabel(l)}</Tag>
    },
    {
      title: 'وضعیت',
      dataIndex: 'isRead',
      key: 'isRead',
      width: 100,
      render: isRead => (
        <Tag color={isRead ? 'gray' : 'gold'}>
          {isRead ? 'خوانده شده' : 'جدید'}
        </Tag>
      )
    },
    {
      title: 'تاریخ ارسال',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: d => new Date(d).toLocaleString('fa-IR')
    },
    {
      title: 'عملیات',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Button size="small" type="primary" onClick={() => openModal(record)}>مشاهده</Button>
          <Popconfirm
            title="آیا از حذف این پیام اطمینان دارید؟"
            onConfirm={() => handleDelete(record._id)}
            okText="بله"
            cancelText="خیر"
          >
            <Button size="small" danger type="text">حذف</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
        <Tag color="gold" style={{ padding: '4px 8px', fontSize: 13 }}>
          پیام‌های جدید (خوانده نشده): {unreadCount}
        </Tag>
      </div>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="_id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: 20,
          total,
          onChange: setPage,
          showSizeChanger: false
        }}
        size="small"
        rowClassName={record => !record.isRead ? 'unread-message-row' : ''}
      />

      <Modal
        title="جزئیات پیام تماس با ما"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setModalOpen(false)}>
            بستن
          </Button>
        ]}
        width={600}
      >
        {selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, background: '#f5f5f5', padding: 12, borderRadius: 8 }}>
              <div><strong>نام:</strong> {selected.name}</div>
              <div><strong>تماس:</strong> {selected.emailOrPhone}</div>
              <div><strong>زبان:</strong> {languageLabel(selected.lang)}</div>
              <div><strong>تاریخ:</strong> {new Date(selected.createdAt).toLocaleString('fa-IR')}</div>
            </div>
            <div>
              <strong>موضوع:</strong>
              <div style={{ marginTop: 4, fontSize: 15, fontWeight: 500 }}>
                {selected.subject || '(بدون موضوع)'}
              </div>
            </div>
            <div>
              <strong>متن پیام:</strong>
              <div style={{
                marginTop: 8,
                padding: 16,
                background: '#fafafa',
                border: '1px solid #e8e8e8',
                borderRadius: 8,
                whiteSpace: 'pre-wrap',
                maxHeight: 300,
                overflowY: 'auto',
                fontSize: 14,
                lineHeight: 1.6
              }}>
                {selected.message}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
